// What an enquiry *is*, and everywhere one goes.
//
// The site is a static bundle on shared hosting — there is no server of ours to
// POST to — so an enquiry is fanned out to third-party endpoints straight from
// the visitor's browser:
//
//   Web3Forms   formats the enquiry as an email and delivers it to the inbox
//               that owns the access key. This is the primary channel: if it
//               fails, the submission failed and the visitor is told so.
//   Apps Script appends a row to a Google Sheet — a searchable log to follow up
//               from later. Strictly secondary and best-effort: a failure here
//               is logged and swallowed, because losing the archive is not a
//               reason to tell a customer their enquiry did not go through.
//
// Both values below are baked into the JS bundle at build time and are readable
// by anyone who views source. That is expected rather than a leak — a Web3Forms
// access key can only ever deliver mail to the address that owns it, and it is
// documented as public. docs/ENQUIRY_SETUP.md covers what that does and does
// not protect against.

import { EMAIL_RE } from "./validation";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || "";
const SHEET_URL = process.env.REACT_APP_ENQUIRY_SHEET_URL || "";
const SHEET_TOKEN = process.env.REACT_APP_ENQUIRY_SHEET_TOKEN || "";

// Direct submission is only offered when there is somewhere to submit to. An
// unconfigured build keeps the old WhatsApp-only behaviour rather than showing
// a button that can only fail — which matters because the key is supplied at
// build time and a missing .env in CI would otherwise break the contact page.
export const canSubmitDirectly = Boolean(WEB3FORMS_KEY);

// Drop empty fields and trim the rest. Returns ordered [label, value] pairs so
// one definition drives the WhatsApp message, the email body and the sheet row
// alike — the three can't drift apart.
function fields(pairs) {
  return pairs
    .filter(([, value]) => value && String(value).trim())
    .map(([label, value]) => [label, String(value).trim()]);
}

// Contact-page form.
export function contactFields(form) {
  return fields([
    ["Name", form.name],
    ["Company", form.company],
    ["Delivery city", form.city],
    ["Contact", form.contact],
    ["Product of interest", form.product],
    ["Requirement", form.requirement],
  ]);
}

// Product enquiry popup. The product and category are the caller's context
// rather than form input, so they are prepended here instead of being typed.
export function productFields(categoryName, item, form) {
  return fields([
    ["Product", item.name],
    ["Category", categoryName],
    ["Name", form.name],
    ["Contact", form.contact],
    ["Company", form.company],
    ["Delivery city", form.city],
    ["Quantity", form.quantity],
    ["Size", form.size],
    ["Grammage", form.grammage],
    ["Description", form.description],
  ]);
}

// Render [label, value] pairs as the body of a WhatsApp message.
function bullets(pairs) {
  return pairs.map(([label, value]) => `• ${label}: ${value}`).join("\n");
}

export function contactMessage(form) {
  const body = bullets(contactFields(form));
  return "New enquiry from the Sigma Enterprises website:" + (body ? `\n\n${body}` : "");
}

export function productMessage(categoryName, item, form) {
  // Product and category lead the message as prose, so they are not repeated in
  // the bullet list underneath.
  const body = bullets(
    productFields(categoryName, item, form).filter(
      ([label]) => label !== "Product" && label !== "Category"
    )
  );
  const head = `Hi Sigma Enterprises, I'd like to enquire about *${item.name}* (${categoryName}).`;
  return head + (body ? `\n\n${body}` : "");
}

// Append a row to the Google Sheet. Never awaited and never throws.
function logToSheet(row) {
  if (!SHEET_URL) return;
  // text/plain keeps this a CORS "simple request". An Apps Script web app
  // cannot answer a preflight OPTIONS, so an application/json body would be
  // rejected by the browser before Google ever saw it. The script JSON.parses
  // the body itself. no-cors because the response is of no interest — the
  // visitor's confirmation must not wait on, or be held up by, Google.
  try {
    fetch(SHEET_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      body: JSON.stringify({ ...row, token: SHEET_TOKEN }),
      keepalive: true,
    }).catch((err) => console.warn("Enquiry sheet log failed:", err));
  } catch (err) {
    console.warn("Enquiry sheet log failed:", err);
  }
}

// Deliver one enquiry. Resolves when the email is accepted; rejects with a
// message worth showing the visitor if it is not.
//
//   kind     short label for the sheet's Type column, e.g. "Product enquiry"
//   subject  subject line of the email that lands in the owner's inbox
//   replyTo  what the visitor gave as their contact; used as the email's
//            Reply-To only when it is actually an email address
//   pairs    ordered [label, value] entries from contactFields/productFields
export async function submitEnquiry({ kind, subject, replyTo, pairs }) {
  if (!WEB3FORMS_KEY) throw new Error("Enquiry form is not configured.");

  const page = typeof window !== "undefined" ? window.location.pathname : "";

  // Fired before the await so the archive is written even when the email leg
  // fails — the sheet is the copy that survives a Web3Forms outage or quota.
  logToSheet({ type: kind, page, fields: Object.fromEntries(pairs) });

  let res;
  try {
    res = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        access_key: WEB3FORMS_KEY,
        subject,
        from_name: "Sigma Enterprises website",
        // Makes Reply in the owner's mail client go to the customer. A phone
        // number is not a valid Reply-To, so it is left off in that case.
        ...(EMAIL_RE.test(String(replyTo || "").trim())
          ? { replyto: String(replyTo).trim() }
          : {}),
        // Honeypot. Web3Forms discards the submission outright when this is
        // non-empty; nothing fills it in a real browser session, while scripted
        // spam fills every field it can find.
        botcheck: "",
        Page: page,
        "Enquiry type": kind,
        ...Object.fromEntries(pairs),
      }),
    });
  } catch (err) {
    // fetch only rejects on a genuine network failure, so this is offline,
    // DNS, or a blocking extension — not a rejection by Web3Forms.
    throw new Error("Couldn't reach the network. Check your connection and try again.");
  }

  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) {
    throw new Error(body.message || `Something went wrong (error ${res.status}).`);
  }
}
